import { CLASS_NAMES, EVENTS, SELECTORS, MISC } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/* global YT */ // Inform ESLint that YT is a global variable

/**
 * A class for the BodyVideo module
 */
export default class BodyVideo extends MessageBus {
  /**
   * Constructor function of the Body Video class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;

    this.loadYouTubeAPI();

    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers, and
   * create the event listeners.
   * @returns {BodyVideo} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   * @returns {BodyVideo} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.videoContainer = this.element.querySelector(`.${CLASS_NAMES.VIDEO}`);
    this.playButton = this.element.querySelector(`.${CLASS_NAMES.VIDEO_PLAY}`);
    this.ytId = this.videoContainer?.dataset.yt;
    this.players = {};
    return this;
  }

  /**
   * Setup event handlers
   * @returns {BodyVideo} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.onPlayButtonClick = this.onPlayButtonClick.bind(this);
    return this;
  }

  /**
   * Enable event listeners
   * @returns {BodyVideo} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (this.playButton) {
      this.playButton.addEventListener(EVENTS.CLICK, this.onPlayButtonClick);
      this.playButton.addEventListener(EVENTS.KEY_DOWN, this.onPlayButtonClick);
    }
    return this;
  }

  /**
   * Loads the YouTube IFrame API script dynamically
   */
  loadYouTubeAPI() {
    if (!window.YT) {
      const tag = document.createElement(SELECTORS.SCRIPT);
      tag.src = MISC.YOUTUBE_URL;
      const firstScriptTag = document.getElementsByTagName(SELECTORS.SCRIPT)[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = () => {
        document
          .querySelectorAll(`.${CLASS_NAMES.VIDEO}`)
          .forEach((element) => {
            const ytId = element.dataset.yt;
            if (ytId) {
              const newId = `yt-${Math.random().toString(10).substring(2, 6)}`;
              element.setAttribute(SELECTORS.ID, newId);
              if (window.YT) {
                this.players[newId] = new YT.Player(newId, {
                  videoId: ytId,
                  playerVars: {
                    rel: 0,
                    showinfo: 0,
                    controls: 1,
                    modestbranding: 1
                  },
                  events: {
                    onReady: (event) => {
                      this.players[newId] = event.target;
                    }
                  }
                });
              }
            }
          });
        window.ytPlayers = this.players;
      };
    }
  }

  /**
   * Handles play button click event
   * @param {Event} event - The click event object
   */
  onPlayButtonClick(event) {
    if (event.type === 'click' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const playerId = this.videoContainer.id;
      const player = window.ytPlayers?.[playerId];

      if (player) {
        player.playVideo();
        this.hideCustomPlayButton();
      }
    }
  }

  /**
   * Hides the custom play button & thumbnail properly
   */
  hideCustomPlayButton() {
    const thumbnails = Array.from(
      this.element.querySelectorAll(`.${CLASS_NAMES.VIDEO_THUMBNAIL}`)
    );

    thumbnails.forEach((thumb) => {
      const thumbnail = thumb;

      thumbnail.style.opacity = '0';
      setTimeout(() => {
        thumbnail.style.visibility = CLASS_NAMES.HIDDEN;
      }, 500);
    });

    if (this.playButton) {
      this.playButton.style.display = MISC.NONE;
      this.playButton.setAttribute('aria-hidden', 'true');
    }
  }
}
